import { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
} from "firebase/firestore";
import InfiniteScroll from "react-infinite-scroll-component";
import { useCollection } from "react-firebase-hooks/firestore";
import { NextSeo } from "next-seo";

import { db } from "utils/firebase";
import { Layout, Loader } from "components/common";
import { useAuth } from "context/AuthContext";
import { subscribeToUser } from "./api/subscription";
import AuthorSummary from "components/Explore/AuthorSummary";

function Explore() {
  const { user, logout } = useAuth();

  const [localUserProfiles, setLocalUserProfiles] = useState<any[]>([]);
  const [lastVisibleUserProfile, setLastVisibleUserProfile] =
    useState<QueryDocumentSnapshot<DocumentData>>(null);
  const [totalUserProfiles, setTotalUserProfiles] = useState(0);

  const [userProfiles, userProfilesLoading] = useCollection(
    query(
      collection(db, "publicUsers"),
      orderBy("lastPostedAt", "desc"),
      limit(11)
    )
  );

  //TODO filter already subscribed
  // const [subscriptionDocs, subscriptionDocsLoading] = useCollection(
  //   query(
  //     collection(db, "publicUsers"),
  //     orderBy("lastPostedAt", "desc"),
  //     limit(11)
  //   )
  // );

  const filteredUserProfiles = userProfiles?.docs.filter(
    (doc) => doc.id !== user?.uid
  );

  // Set total public user count
  useEffect(() => {
    const userProfileCountQuery = lastVisibleUserProfile
      ? query(
          collection(db, "publicUsers"),
          orderBy("lastPostedAt", "desc"),
          startAfter(lastVisibleUserProfile)
        )
      : query(collection(db, "publicUsers"), orderBy("lastPostedAt", "desc"));
    const getTotalCount = async () =>
      await getCountFromServer(userProfileCountQuery).then((snap) =>
        setTotalUserProfiles(snap.data().count)
      );
    getTotalCount();
  }, [lastVisibleUserProfile]);

  // Concat new userProfiles to local data
  useEffect(() => {
    setLocalUserProfiles((localUserProfileData) =>
      localUserProfileData.concat(userProfiles?.docs || [])
    );
  }, [userProfiles]);

  const handleFetchNextUserProfiles = () => {
    if (userProfiles?.docs.length) {
      setLastVisibleUserProfile(
        userProfiles.docs[userProfiles.docs.length - 1]
      );
    }
  };

  const renderAuthors = () => {
    return filteredUserProfiles?.length ? (
      filteredUserProfiles.map((doc, index) => {
        const handleSubscribe = async () => {
          return await subscribeToUser(user?.uid, doc.id);
        };

        const isLastItem = index >= filteredUserProfiles?.length - 1;

        return (
          <div
            key={doc.id}
            className={`relative flex h-fit w-full justify-center ${
              isLastItem ? "border-none" : "border-b"
            } border-gray-200`}
          >
            <AuthorSummary
              subscriptionId={doc.id}
              onSubscribeClick={handleSubscribe}
            />
          </div>
        );
      })
    ) : (
      <div className="text-center">No new authors found</div>
    );
  };

  return (
    <Layout>
      <NextSeo
        title="Explore"
        description="Explore Act Now Button users who have recently posted"
      />

      <div className="flex min-h-full w-full flex-col items-center justify-start py-8 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h1 className="mt-3 px-4 py-2 text-center text-xl font-medium tracking-tight text-black sm:mt-6 ">
            Explore Authors
          </h1>
        </div>
        {userProfilesLoading ? (
          <div className="mt-8 flex w-full items-center justify-center">
            <Loader />
          </div>
        ) : (
          <InfiniteScroll
            className="mt-8 flex w-full flex-col items-center justify-center gap-y-2"
            dataLength={localUserProfiles.length}
            next={handleFetchNextUserProfiles}
            hasMore={userProfiles?.docs?.length < totalUserProfiles}
            loader={<div className="flex justify-center">Loading more...</div>}
          >
            {renderAuthors()}
          </InfiniteScroll>
        )}
      </div>
    </Layout>
  );
}

export default Explore;
